import React, { useEffect, useLayoutEffect, useMemo } from 'react';

export type OrdenBeneficenciaFieldKey =
  | 'patientName'
  | 'consultDate'
  | 'ordenTexto'
  | 'doctorName';

export interface FieldPosition {
  top: number;
  left: number;
  width?: number;
}

export interface OrdenBeneficenciaCanvasProps {
  patientName: string;
  consultDate: string;
  ordenTexto: string;
  doctorName: string;
  className?: string;
  editMode?: boolean;
  selectedField?: OrdenBeneficenciaFieldKey | null;
  onSelectField?: (field: OrdenBeneficenciaFieldKey) => void;
  fieldPositions?: Partial<Record<OrdenBeneficenciaFieldKey, FieldPosition>>;
  onFieldBoundsChange?: (field: OrdenBeneficenciaFieldKey, size: { width: number; height: number }) => void;
}

export const DEFAULT_ORDEN_BENEFICENCIA_POSITIONS: Record<OrdenBeneficenciaFieldKey, FieldPosition> = {
  patientName: { top: 275, left: 124 },
  consultDate: { top: 275, left: 1350, width: 350 },
  ordenTexto: { top: 560, left: 140 },
  doctorName: { top: 1588, left: 112 },
};

export const ORDEN_BENEFICENCIA_FIELD_KEYS = Object.keys(DEFAULT_ORDEN_BENEFICENCIA_POSITIONS) as OrdenBeneficenciaFieldKey[];

const OrdenBeneficenciaCanvas: React.FC<OrdenBeneficenciaCanvasProps> = ({
  patientName,
  consultDate,
  ordenTexto,
  doctorName,
  className = '',
  editMode = false,
  selectedField,
  onSelectField,
  fieldPositions,
  onFieldBoundsChange,
}) => {
  const mergedPositions = useMemo(() => {
    return ORDEN_BENEFICENCIA_FIELD_KEYS.reduce<Record<OrdenBeneficenciaFieldKey, FieldPosition>>((acc, key) => {
      const base = DEFAULT_ORDEN_BENEFICENCIA_POSITIONS[key];
      const override = fieldPositions?.[key];
      acc[key] = override ? { ...base, ...override } : { ...base };
      return acc;
    }, {} as Record<OrdenBeneficenciaFieldKey, FieldPosition>);
  }, [fieldPositions]);

  const handleSelect = (field: OrdenBeneficenciaFieldKey) => {
    if (editMode) {
      onSelectField?.(field);
    }
  };

  const refs = useMemo(() => {
    return ORDEN_BENEFICENCIA_FIELD_KEYS.reduce<Record<OrdenBeneficenciaFieldKey, React.RefObject<HTMLDivElement>>>((acc, key) => {
      acc[key] = React.createRef<HTMLDivElement>();
      return acc;
    }, {} as Record<OrdenBeneficenciaFieldKey, React.RefObject<HTMLDivElement>>);
  }, []);

  useLayoutEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    (Object.keys(refs) as OrdenBeneficenciaFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (el) {
        const rect = el.getBoundingClientRect();
        onFieldBoundsChange(key, { width: rect.width, height: rect.height });
      }
    });
  }, [patientName, consultDate, ordenTexto, doctorName, editMode, refs, onFieldBoundsChange]);

  useEffect(() => {
    if (!onFieldBoundsChange) {
      return;
    }

    const observers: ResizeObserver[] = [];

    (Object.keys(refs) as OrdenBeneficenciaFieldKey[]).forEach((key) => {
      const el = refs[key].current;
      if (!el) {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          onFieldBoundsChange(key, { width, height });
        }
      });

      observer.observe(el);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [refs, onFieldBoundsChange]);

  const classSuffix: Record<OrdenBeneficenciaFieldKey, string> = {
    patientName: 'patient-name',
    consultDate: 'consult-date',
    ordenTexto: 'orden-texto',
    doctorName: 'doctor-name',
  };

  const styleFor = (field: OrdenBeneficenciaFieldKey): React.CSSProperties => {
    const pos = mergedPositions[field];
    const base: React.CSSProperties = {
      top: `${pos.top / 100}cm`,
      left: `${pos.left / 100}cm`,
    };
    if (pos.width != null) {
      base.width = `${pos.width / 100}cm`;
    }
    if (editMode) {
      base.cursor = 'move';
    }
    return base;
  };

  const focusableProps = (field: OrdenBeneficenciaFieldKey) => {
    const baseClass = `recipe-field orden-beneficencia-field-${classSuffix[field]}`;

    if (editMode) {
      return {
        tabIndex: 0,
        onClick: () => handleSelect(field),
        onFocus: () => handleSelect(field),
        className: `${baseClass} ${selectedField === field ? 'recipe-field-selected' : ''}`.trim(),
      };
    }

    return {
      className: baseClass,
    };
  };

  return (
    <div
      className={`recipe-print-page orden-beneficencia ${editMode ? 'recipe-print-page--edit' : ''} ${className}`.trim()}
    >
      <div ref={refs.patientName} style={styleFor('patientName')} {...focusableProps('patientName')}>
        {patientName}
      </div>
      <div ref={refs.consultDate} style={{ ...styleFor('consultDate'), fontSize: '11pt' }} {...focusableProps('consultDate')}>
        {consultDate}
      </div>
      <div ref={refs.ordenTexto} style={styleFor('ordenTexto')} {...focusableProps('ordenTexto')}>
        <span className="whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', tabSize: 38 }}>{ordenTexto}</span>
      </div>
      <div ref={refs.doctorName} style={{ ...styleFor('doctorName'), fontSize: '11pt' }} {...focusableProps('doctorName')}>
        {doctorName}
      </div>
    </div>
  );
};

export default OrdenBeneficenciaCanvas;
